import { styled, Typography } from '@mui/material'
import { ComponentType, memo, PureComponent } from 'react'
import { createViewModeList, createViewModeModule } from './view-mode.content'
import { EViewMode, IViewModeConfigs, IViewModeValidModel } from './view-mode.types'
import { LoadingError, LoadingNoData, LoadingOverlay, LoadingWaiting } from './view-mode.units'
import AnimationSwitch from './animation-switch'

interface IViewModeSlots<T extends IViewModeValidModel> extends IViewModeConfigs<T> {}

/**
 * Props for ViewMode component
 * @template T - Type extending IViewModeValidModel
 */
interface IViewModeProps<T extends IViewModeValidModel> {
  data: T[]
  viewMode?: EViewMode
  ready?: boolean
  autoHeight?: boolean
  loading?: boolean
  error?: string
  slots?: IViewModeSlots<T>
}

export function createViewMode<T extends IViewModeValidModel>(configs?: IViewModeConfigs<T>): ComponentType<IViewModeProps<T>> {
  // Pre-memoized components for better performance
  const MemoizedContentModeModule = createViewModeModule<T>()
  const MemoizedContentModeList = createViewModeList<T>()

  type State = {
    currentMode: EViewMode
  }

  class ViewMode extends PureComponent<IViewModeProps<T>, State> {
    constructor(props: IViewModeProps<T>) {
      super(props)
      this.state = { currentMode: props.viewMode || EViewMode.Module }
    }

    componentDidUpdate(prevProps: IViewModeProps<T>) {
      if (prevProps.viewMode !== this.props.viewMode && this.props.viewMode) {
        this.setState({ currentMode: this.props.viewMode })
      }
    }

    getInternalConfigs() {
      const columns = Object.assign({}, configs?.list?.columns, this.props.slots?.list?.columns)
      const listConfigs = Object.assign({}, configs?.list, this.props.slots?.list, { columns })
      return {
        NoData: this.props.slots?.NoData ?? configs?.NoData ?? LoadingNoData,
        Skeleton: this.props.slots?.Skeleton ?? configs?.Skeleton ?? LoadingWaiting,
        ErrorData: this.props.slots?.ErrorData ?? configs?.ErrorData ?? LoadingError,
        listConfigs,
        moduleConfigs: Object.assign({}, configs?.module, this.props.slots?.module)
      }
    }

    getWrapperProps() {
      return Object.assign({}, configs?.wrapProps, this.props.slots?.wrapProps)
    }

    renderContentByMode(currentMode: EViewMode) {
      const internalConfigs = this.getInternalConfigs()

      switch (currentMode) {
        case EViewMode.List:
          return <MemoizedContentModeList autoHeight={this.props.autoHeight} value={this.props.data} configs={internalConfigs.listConfigs} />
        case EViewMode.Module:
          return <MemoizedContentModeModule autoHeight={this.props.autoHeight} data={this.props.data} configs={internalConfigs.moduleConfigs} />
        default:
          return <Typography variant='subtitle1'>Unsupported view mode: {String(currentMode)}</Typography>
      }
    }

    getContentState(currentMode: EViewMode) {
      const { NoData, ErrorData, Skeleton } = this.getInternalConfigs()

      if (this.props.error) {
        return { key: 'error', content: <ErrorData error={this.props.error} /> }
      }

      if (this.props.loading && this.props.data.length === 0 && Skeleton) {
        return { key: 'skeleton', content: <Skeleton mode={currentMode} /> }
      }

      if (this.props.data.length === 0 && !this.props.loading) {
        return { key: 'no-data', content: <NoData mode={currentMode} /> }
      }

      if (this.props.data.length > 0) {
        return {
          key: `content-${currentMode}-${this.props.data.length}`,
          content: (
            <>
              {this.props.loading && <LoadingOverlay />}
              {/* Use AnimationSwitch to handle crossfade + measurement */}
              <AnimationSwitch
                autoHeight={this.props.autoHeight}
                mode={currentMode}
                moduleNode={this.renderContentByMode(EViewMode.Module)}
                listNode={this.renderContentByMode(EViewMode.List)}
              />
            </>
          )
        }
      }

      return { key: 'loading', content: <LoadingOverlay /> }
    }

    render() {
      const wrapperProps = this.getWrapperProps()

      const { key, content } = this.getContentState(this.state.currentMode)

      return (
        <Wrap {...(wrapperProps as any)} data-key={key}>
          {content}
        </Wrap>
      )
    }
  }

  return memo(ViewMode as unknown as ComponentType<IViewModeProps<T>>)
}

const Wrap = styled('div', {
  // Only forward specific props to prevent MUI system props from being passed to DOM
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'bgcolor' && prop !== 'sx'
})(({ theme }) => ({
  position: 'relative',
  // Remove overflow hidden to allow natural animations
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '100px',
  flex: 1,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  // overflow: 'hidden'
}))
